import type { MentionUser } from "@/components/AtMention/types";
import {
  facilityMatchesQuery,
  isConcatenatedFacilityQuery,
  isStrongFacilityIntentQuery,
  personNameMatchesQuery,
  workplaceMatchesQuery
} from "./quickAddMentionMatch";
import {
  normalizeSectionName,
  parseStateFromLocation,
  statesForSectionName,
  US_STATE_ADJACENCY
} from "./pgaSectionStates";

export const GEO_SCORE = {
  consultantSection: 400,
  assignedSection: 300,
  sameState: 200,
  adjacentState: 100
} as const;

export const NAME_SCORE = {
  prefix: 150,
  contains: 50,
  workplace: 25
} as const;

export const TEAMMATE_BOOST = 80;

export const FACILITY_MATCH_BOOST = 600;

export type GeoProximityContext = {
  consultantSectionNames: string[];
  assignedSectionNames: string[];
  userLocationState: string | null;
};

function mentionDisplayName(user: MentionUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
}

function normalizedSet(names: string[]): Set<string> {
  return new Set(names.map((name) => normalizeSectionName(name)).filter(Boolean));
}

function proximityStates(ctx: GeoProximityContext): Set<string> {
  const states = new Set<string>();
  for (const name of [...ctx.consultantSectionNames, ...ctx.assignedSectionNames]) {
    for (const state of statesForSectionName(name)) {
      states.add(state);
    }
  }
  if (ctx.userLocationState) {
    states.add(ctx.userLocationState.toUpperCase());
  }
  return states;
}

function hitState(user: MentionUser): string | null {
  return parseStateFromLocation(user.state);
}

export function geoScore(user: MentionUser, ctx: GeoProximityContext): number {
  const consultant = normalizedSet(ctx.consultantSectionNames);
  const assigned = normalizedSet(ctx.assignedSectionNames);
  const sectionKey = normalizeSectionName(user.sectionName);
  if (sectionKey && consultant.has(sectionKey)) return GEO_SCORE.consultantSection;
  if (sectionKey && assigned.has(sectionKey)) return GEO_SCORE.assignedSection;

  const state = hitState(user);
  if (!state) return 0;

  const nearby = proximityStates(ctx);
  if (nearby.has(state)) return GEO_SCORE.sameState;

  const adjacent = US_STATE_ADJACENCY[state] ?? [];
  if (adjacent.some((neighbor) => nearby.has(neighbor))) return GEO_SCORE.adjacentState;

  return 0;
}

export function nameScore(user: MentionUser, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  if (user.kind === "facility") {
    return facilityMatchesQuery(mentionDisplayName(user), query) ? NAME_SCORE.prefix : 0;
  }

  if (user.kind === "person") {
    if (personNameMatchesQuery(user, query)) {
      const first = user.firstName.toLowerCase();
      const last = user.lastName.toLowerCase();
      const full = mentionDisplayName(user).toLowerCase();
      if (full.startsWith(q) || first.startsWith(q) || last.startsWith(q)) return NAME_SCORE.prefix;
      return NAME_SCORE.contains;
    }
    if (workplaceMatchesQuery(user.facilityName, query)) return NAME_SCORE.workplace;
    return 0;
  }

  const first = user.firstName.toLowerCase();
  const last = user.lastName.toLowerCase();
  const full = mentionDisplayName(user).toLowerCase();
  if (full.startsWith(q) || first.startsWith(q) || last.startsWith(q)) return NAME_SCORE.prefix;
  if (full.includes(q) || first.includes(q) || last.includes(q)) return NAME_SCORE.contains;
  return 0;
}

function kindTieBreak(user: MentionUser): number {
  switch (user.kind) {
    case "teammate":
    case undefined:
      return 3;
    case "person":
      return 2;
    case "facility":
      return 1;
    case "event":
      return 0;
    default:
      return 0;
  }
}

export function rankScore(user: MentionUser, query: string, ctx: GeoProximityContext): number {
  const teammateBoost = !user.kind || user.kind === "teammate" ? TEAMMATE_BOOST : 0;
  const facilityBoost =
    user.kind === "facility" &&
    facilityMatchesQuery(mentionDisplayName(user), query) &&
    (isStrongFacilityIntentQuery(query) || isConcatenatedFacilityQuery(mentionDisplayName(user), query))
      ? FACILITY_MATCH_BOOST
      : 0;
  return geoScore(user, ctx) + nameScore(user, query) + teammateBoost + facilityBoost;
}

export function rankQuickAddMentions(
  users: MentionUser[],
  query: string,
  ctx: GeoProximityContext,
  limit = 12
): MentionUser[] {
  const back = users.filter((user) => user.kind === "back" || user.selectAction === "back");
  const rest = users.filter((user) => user.kind !== "back" && user.selectAction !== "back");
  const ranked = [...rest].sort((a, b) => {
    const scoreDelta = rankScore(b, query, ctx) - rankScore(a, query, ctx);
    if (scoreDelta !== 0) return scoreDelta;
    const kindDelta = kindTieBreak(b) - kindTieBreak(a);
    if (kindDelta !== 0) return kindDelta;
    return mentionDisplayName(a).localeCompare(mentionDisplayName(b));
  });
  return [...back, ...ranked.slice(0, limit)];
}
